import { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { OfficialGroup } from "../../shared/contracts";
import { StatePanel } from "../components/StatePanel";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { toast } from "../lib/toast";

export function GroupsPage() {
  const [groups, setGroups] = useState<OfficialGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, requestLogin } = useApp();
  const navigate = useNavigate();
  const load = useCallback(async () => { setLoading(true); setError(null); try { setGroups((await api<{ items: OfficialGroup[] }>("/groups")).items); } catch (e) { setError(e instanceof Error ? e.message : "社群加载失败"); } finally { setLoading(false); } }, []);
  useEffect(() => { void load(); }, [load, user?.id]);
  const enter = async (group: OfficialGroup) => {
    if (!user) { requestLogin("请先登录账号，才可以加入官方群。", `/groups/${group.id}`); return; }
    try {
      const justJoined = !group.joined;
      if (justJoined) await api(`/groups/${group.id}/join`, { method: "POST" });
      navigate(`/groups/${group.id}`, justJoined ? { state: { justJoined: true } } : undefined);
    }
    catch (e) { toast(e instanceof Error ? e.message : "无法加入社群", "danger"); }
  };
  return (
    <div className="page-shell content-page groups-page">
      <header className="page-intro groups-intro">
        <span className="eyebrow">平台预设 · 用户不可自建</span>
        <h1>官方游戏交流社群</h1>
        <p>加入对应游戏社群，交流账号信息，选购商品请前往商城下单。</p>
      </header>
      <Alert severity="warning"><strong>社区提醒：</strong>官方群用于同好交流，禁止发布站外联系方式或引导私下转账，违规账号将限制发布或被封禁。</Alert>
      <div className="section-heading"><div><h2>可加入的官方群</h2></div><span>{groups.length} 个社群</span></div>
      {loading ? <StatePanel kind="loading" /> : null}
      {error ? <StatePanel kind="error" description={error} onRetry={() => void load()} /> : null}
      {!loading && !error && !groups.length ? <StatePanel kind="empty" title="暂时没有开放的官方群" /> : null}
      <div className="group-grid">
        {groups.map((group) => (
          <Card className="group-card" key={group.id}>
            <div className={`group-cover${group.coverUrl ? " has-image" : ""}`}>
              {group.coverUrl ? <img src={group.coverUrl} alt={`${group.name}封面`} /> : <span aria-hidden="true">{group.gameName.slice(0, 1)}</span>}
            </div>
            <CardContent className="group-body">
              <span className="panel-kicker">{group.gameName}</span>
              <div className="group-title"><span className="group-icon">{group.gameName.slice(0, 1)}</span><div><h3>{group.name}</h3><p>{group.memberCount} 位成员{group.unreadCount ? ` · ${group.unreadCount} 条未读` : ""}</p></div></div>
              <p>{group.description}</p>
              <Button variant={group.joined ? "outlined" : "contained"} type="button" startIcon={group.joined ? <Check aria-hidden="true" /> : undefined} onClick={() => void enter(group)}>{group.joined ? "已加入，进入" : "加入社群"}</Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
